'use client';

import { ActionBar, ActionBarActions, Button, Icon } from '@lulalife/design-system';
import { InfoCircle } from '@lulalife/design-system/icons/line';
import { useCoordinatorShell } from 'src/components/Layout/PrivateLayout/CoordinatorShellContext';
import { useInsideSettingsPage } from './SettingsPage';

type SettingsSaveBarProps = {
  /** Renders the bar. Off, nothing is in the DOM at all — see the note below. */
  visible: boolean;
  /** Left-hand copy. Defaults to the prototype's wording. */
  message?: string;
  /** Disables both actions and puts the primary button in its loading state. */
  isSaving?: boolean;
  /**
   * Whether the draft can be committed at all. Off, Save is disabled and only
   * Discard is offered — for a surface holding a value the API would reject, so
   * the reader is not sent to the server to be told so.
   */
  canSave?: boolean;
  onSave: () => void;
  onDiscard: () => void;
  'data-testid'?: string;
  className?: string;
};

/**
 * Sticky unsaved-changes footer, full width to the side nav's edge (the pane's 24px inset is
 * pulled back) for Settings forms: DS `ActionBar` with the status message as the
 * `leading` slot and right-aligned Discard / Save actions.
 *
 * The bar sticks to the bottom of AppShell's content scrollport. It unmounts
 * while clean so it neither reserves space nor announces an empty status.
 */
/**
 * How far the bar must reach back to touch the pane edges: exactly the padding around it, so it
 * never overflows. The pane (`.ls-data-content`) gives 16px, none under the coordinator shell; the
 * page adds its 8px inset when the bar sits inside a `SettingsPage`, and not when it is the page's
 * sibling. Whole class names, because Tailwind reads this file as text.
 */
const reachBackClass = (insidePage: boolean, coordinatorShell: boolean): string => {
  // Figma runs the bar edge to edge and flush with the bottom of the content area, so it pulls
  // back through the shell's 24px side and bottom padding.
  if (coordinatorShell) return '-mx-space-24 -mb-space-24';
  return insidePage ? '-mx-space-24' : '-mx-space-16';
};

export const SettingsSaveBar = ({
  visible,
  message = 'You have unsaved changes',
  isSaving = false,
  canSave = true,
  onSave,
  onDiscard,
  'data-testid': dataTestId,
  className,
}: SettingsSaveBarProps) => {
  const insidePage = useInsideSettingsPage();
  const coordinatorShell = useCoordinatorShell() !== null;
  if (!visible) return null;

  return (
    <div
      data-lula-ds=""
      data-testid={dataTestId}
      role="status"
      // The space before the interpolation is load-bearing; see the note in
      // SettingsRow. Tailwind reads this file as text, and a one-off utility
      // written flush against a `${` is never extracted, so the class is never
      // generated and silently does nothing.
      className={`sticky bottom-0 z-20 ${reachBackClass(insidePage, coordinatorShell)} mt-space-24 font-sans ${className ?? ''}`}
    >
      <ActionBar>
        <ActionBarActions
          leading={
            <span className="typography-body-sm flex min-w-0 items-center gap-space-8 text-text-secondary">
              <Icon size="sm" aria-hidden="true" className="text-icon-secondary">
                <InfoCircle />
              </Icon>
              {message}
            </span>
          }
        >
          <Button variant="secondary" type="button" disabled={isSaving} onClick={onDiscard}>
            Discard
          </Button>
          <Button
            variant="primary"
            type="button"
            isLoading={isSaving}
            disabled={!canSave || isSaving}
            onClick={onSave}
          >
            Save changes
          </Button>
        </ActionBarActions>
      </ActionBar>
    </div>
  );
};

export default SettingsSaveBar;
